/**
 * Where the monthly export's day columns sit, and how to keep stored widths pointing at them.
 *
 * `columnWidths` is keyed by ABSOLUTE column index because that is the shape the backend consumes
 * (`monthly_column_widths` in `backend/services/downloads.py` looks up `str(index)`). The leading
 * columns are Location, Role, Name and — only when it is switched on — Hire Date, so turning that
 * column on or off shifts every day column by one and silently re-points every stored width at its
 * neighbour. These helpers are the one place that arithmetic lives.
 */

/** Location, Role, Name — always present, in this order. */
const ALWAYS_LEADING_COLUMNS = 3;

/** Index of the first day column, given whether the hire-date column is showing. */
export const firstDayColumnIndex = (showHireDate: boolean): number => (showHireDate ? ALWAYS_LEADING_COLUMNS + 1 : ALWAYS_LEADING_COLUMNS);

/** Every day column set to one width, merged over the existing overrides by the caller. */
export const dayColumnWidths = (widthPx: number, showHireDate: boolean, dayCount: number): Record<string, number> => {
	const first = firstDayColumnIndex(showHireDate);
	return Object.fromEntries(Array.from({ length: dayCount }, (_unused, offset) => [String(first + offset), widthPx]));
};

/**
 * Move stored widths so they still describe the same columns after the hire-date column appears or
 * disappears.
 *
 * Turning it ON inserts a column at index 3, so every day width shifts up one. Turning it OFF removes
 * that column: the width stored FOR it has nothing left to describe and is dropped, and every day
 * width shifts back down. The three always-present leading columns never move.
 */
export const reindexForHireDate = (widths: Record<string, number>, nextShowHireDate: boolean): Record<string, number> => {
	const shift = nextShowHireDate ? 1 : -1;
	const reindexed: Record<string, number> = {};

	for (const [key, width] of Object.entries(widths)) {
		const index = Number(key);
		if (index < ALWAYS_LEADING_COLUMNS) {
			reindexed[key] = width;
			continue;
		}
		// Removing the hire-date column drops the width that belonged to it rather than sliding it onto
		// the first day column, which would silently resize a column the user never touched.
		if (!nextShowHireDate && index === ALWAYS_LEADING_COLUMNS) continue;
		reindexed[String(index + shift)] = width;
	}

	return reindexed;
};
